/**
 * Periyodik yoklama yardımcıları.
 *
 * Sunucu soğuk katman erişilebilirliğini **anlık ölçümle** bildirir; istemci
 * bunu `cacheTtlMs` (30 sn) boyunca önbellekler. Bu yüzden istemci de periyodik
 * sormalı — aksi hâlde tünel düştüğünde ekran 30 saniye boyunca yanlış bilgi
 * gösterir ve kullanıcı oynatmaya çalışıp 503 alır.
 *
 * Saf (saf olmayan) parçalar test edilir; zamanlayıcı yönetimi React'ta.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** Sunucunun önbellek süresiyle hizalı yoklama aralığı (ms). */
export const LAYER_POLL_INTERVAL_MS = 30_000;

/** Sekme arka plandayken yoklama yapılmaz (pil ve sunucu yükü). */
export function shouldPollNow(visibility: string, now: number, lastAt: number | null): boolean {
  if (visibility === 'hidden') return false;
  return lastAt === null || now - lastAt >= LAYER_POLL_INTERVAL_MS;
}

/** İlk yoklamanın gecikmesi: beklemek anlamsız, hemen denenir. */
export function initialPollDelayMs(lastAt: number | null, now: number): number {
  if (lastAt === null) return 0;
  const remaining = LAYER_POLL_INTERVAL_MS - (now - lastAt);
  return remaining > 0 ? remaining : 0;
}

export interface PolledValue<T> {
  value: T | null;
  /** İlk yükleme sürüyor (yenileme sessizdir). */
  loading: boolean;
  error: string | null;
  /** Son başarılı ölçüm zamanı (epoch ms). */
  updatedAt: number | null;
  /** Elle yenileme (kullanıcı "yenile" dediğinde). */
  refresh: () => void;
}

/**
 * Bir uçtan periyodik veri çeker.
 *
 * - Sekme görünür olduğunda yoklar; arka planda **durmaz**, geri gelince
 *   hemen tazeler (30 sn beklemeden).
 * - Hata olursa değerini **kaybetmez**: son başarılı veri ekranda kalır,
 *   `error` ayrıca bildirilir. Yoksa tünel dalgalanmasında arayüz boşalır.
 */
export function usePolledValue<T>(
  fetcher: () => Promise<T>,
  options: { intervalMs?: number } = {},
): PolledValue<T> {
  const intervalMs = options.intervalMs ?? LAYER_POLL_INTERVAL_MS;
  const [value, setValue] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);

  // `fetcher` her render'da yeni bir referans alabilir; son çağrıyı ref'te tutarız.
  const fetchRef = useRef(fetcher);
  fetchRef.current = fetcher;
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const next = await fetchRef.current();
      if (!mountedRef.current) return;
      setValue(next);
      setUpdatedAt(Date.now());
      setError(null);
    } catch (caught) {
      if (!mountedRef.current) return;
      // Son değer korunur; yalnız hata bildirilir.
      setError(caught instanceof Error ? caught.message : 'Yenilenemedi');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let timer: number | undefined;

    const tick = async (): Promise<void> => {
      await load();
      if (!mountedRef.current) return;
      timer = window.setTimeout(() => void tick(), intervalMs);
    };

    const onVisible = (): void => {
      if (document.visibilityState === 'hidden') return;
      // Sekme geri geldi: beklemeden tazele, zamanlayıcıyı yeniden kur.
      window.clearTimeout(timer);
      void tick();
    };

    void tick();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load, intervalMs]);

  const refresh = useCallback(() => {
    void load();
  }, [load]);

  return { value, loading, error, updatedAt, refresh };
}
